import React, { memo, useMemo } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { CustomButtonProps } from 'types/props/CustomButtonProps';
import { colors } from 'styles/colors';

function CustomButton({
  text,
  variant = 'filled',
  disabled = false,
  isLoading = false,
  intent = 'primary',
  icon,
  fontSize = 14,
  textColor,
  fontWeight = 500,
  height = 45,
  type = 'button',
  radius = 10,
  ...props
}: CustomButtonProps) {
  const theme = useTheme();

  const muiVariant = variant === 'outlined' ? 'outlined' : 'contained';

  const resolvedTextColor =
    variant === 'outlined'
      ? colors[intent]
      : textColor || theme.palette.common.white;

  const sxStyles = useMemo(() => {
    return {
      height,
      fontSize,
      fontWeight,
      textTransform: 'none',
      color: resolvedTextColor,
      borderRadius: `${radius}px`,
      paddingRight: '30px',
      paddingLeft: '30px',
      minWidth: '100px',

      /* Generic hover fallback */
      '&:hover': {
        color: resolvedTextColor,
      },

      /* IMPORTANT: variant-specific hover overrides */
      '&.MuiButton-outlined:hover': {
        color: resolvedTextColor,
      },

      '&.MuiButton-contained:hover': {
        color: resolvedTextColor,
      },

      /* Keep icons from changing color */
      '&:hover .MuiButton-startIcon, &:hover .MuiButton-endIcon': {
        color: resolvedTextColor,
      },

      ...(variant === 'outlined' && {
        borderColor: colors[intent],
      }),

      ...(intent === 'transparent' && {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }),

      /* A loading button is a disabled button, and the two have to land
         together. The colours a button is disabled in are reached through the
         same quarter-second fade every other state change uses, so the spinner
         appeared on a button that still looked pressable and the grey caught up
         afterwards. Nothing is being animated towards here - the press is over
         and the button is out of use - so the fade is dropped. */
      ...(isLoading && {
        transition: 'none',
      }),
    };
  }, [height, fontSize, fontWeight, resolvedTextColor, variant, intent, isLoading]);

  return (
    <Button
      type={type}
      variant={muiVariant}
      disabled={disabled || isLoading}
      startIcon={!isLoading ? icon : undefined}
      sx={sxStyles}
      {...props}
    >
      {isLoading ? (
        <CircularProgress
          size={24}
          sx={{
            color:
              variant === 'outlined'
                ? colors[intent]
                : theme.palette.common.white,
          }}
        />
      ) : (
        <Box component="span">{text}</Box>
      )}
    </Button>
  );
}

export default memo(CustomButton);
